import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  Search,
  Building2,
  MapPin,
  Briefcase,
  Clock,
  Inbox,
  AlertTriangle,
  RefreshCw,
  ArrowRight,
  LogIn,
  LogOut,
  User as UserIcon,
} from 'lucide-react';

import { formatSalaryDisplay } from '@/CompanyAdmin/utils/salaryUtils';
import { Card, CardContent } from '@/Components/ui/card';
import { Button } from '@/Components/ui/button';
import { Badge } from '@/Components/ui/badge';
import { Input } from '@/Components/ui/input';
import { Skeleton } from '@/Components/ui/skeleton';
import { Separator } from '@/Components/ui/separator';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/Components/ui/dropdown-menu';
import { cn } from '@/lib/utils';

import { listAllJobs, getMe, logoutCandidate } from './publicJobsApi';
import { PublicJobDetailDialog } from './PublicJobDetailDialog';
import { CandidateLoginDialog } from './CandidateLoginDialog';

/**
 * /jobs — master listing for the public job board.
 *
 * Jobs are fetched from /api/public/jobs and grouped on the client by
 * company so each company is its own collapsible-feeling section. The
 * URL pattern for deep-linking remains `/c/<slug>/<job_id>`, which is
 * served by `CompanyJobsPage`. Clicking a card here opens the detail
 * dialog inline (no navigation) for a snappy browse-many flow.
 */
export default function PublicJobsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  // `/jobs/:jobId` deep-link param. When present (e.g. a shared recruiter
  // link), we open that job's detail dialog directly on first paint.
  const params = useParams();

  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [query, setQuery] = useState(searchParams.get('q') || '');

  // For inline JD modal — null means closed. Seeded from the `/jobs/:jobId`
  // URL param so a shared deep link opens that job immediately.
  const [activeJobId, setActiveJobId] = useState(params.jobId || null);
  const [activeCompanySlug, setActiveCompanySlug] = useState(null);

  // Keep the open dialog in sync with the URL param so browser back/forward
  // (and landing directly on /jobs/<id>) work as expected.
  useEffect(() => {
    setActiveJobId(params.jobId || null);
  }, [params.jobId]);

  // Debounced fetch — fires on first mount and whenever the URL `?q=`
  // param changes. We sync `query` → URL on every keystroke (300ms
  // debounce) so the listing is shareable.
  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await listAllJobs({ q: searchParams.get('q') || '' });
        if (!cancelled) setJobs(data.jobs || []);
      } catch (e) {
        console.error('listAllJobs failed:', e);
        if (!cancelled) setError(e.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [searchParams]);

  // Push the query into the URL (debounced) so refreshing keeps the search.
  useEffect(() => {
    const t = setTimeout(() => {
      const sp = new URLSearchParams(searchParams);
      if (query) sp.set('q', query);
      else sp.delete('q');
      setSearchParams(sp, { replace: true });
    }, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query]);

  // Group by company name for the section layout.
  const grouped = useMemo(() => {
    const map = new Map();
    for (const j of jobs) {
      const key = j.company_name || 'Other';
      if (!map.has(key)) {
        map.set(key, { name: key, slug: j.company_slug, items: [] });
      }
      map.get(key).items.push(j);
    }
    return [...map.values()].sort((a, b) => a.name.localeCompare(b.name));
  }, [jobs]);

  const openJob = (job) => {
    setActiveJobId(job.job_id);
    setActiveCompanySlug(job.company_slug);
  };

  return (
    <div className="min-h-screen bg-slate-50/50 dark:bg-slate-950">
      <PublicHeader />

      {/* Hero */}
      <section className="border-b border-border/40 bg-card">
        <div className="mx-auto flex max-w-6xl flex-col gap-4 px-4 py-10 sm:py-14">
          <h1 className="text-3xl font-bold tracking-tight sm:text-4xl">
            Find your next opportunity
          </h1>
          <p className="text-sm text-muted-foreground sm:text-base">
            Browse {jobs.length || ''} active roles across companies hiring right now.
          </p>

          <div className="relative max-w-xl">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Search by title, company or location…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="h-11 pl-9"
            />
          </div>
        </div>
      </section>

      {/* List */}
      <section className="mx-auto max-w-6xl px-4 py-8">
        {loading ? (
          <ListSkeleton />
        ) : error ? (
          <ErrorBlock message={error} />
        ) : grouped.length === 0 ? (
          <EmptyBlock query={query} />
        ) : (
          <div className="space-y-8">
            {grouped.map((group) => (
              <CompanyGroup
                key={group.name}
                group={group}
                onJobClick={openJob}
                onCompanyClick={() => navigate(`/c/${group.slug}`)}
              />
            ))}
          </div>
        )}
      </section>

      <PublicFooter />

      {/* Inline detail dialog */}
      {activeJobId && (
        <PublicJobDetailDialog
          jobId={activeJobId}
          companySlug={activeCompanySlug}
          onClose={() => {
            setActiveJobId(null);
            setActiveCompanySlug(null);
            // If we arrived via a /jobs/<id> deep link, drop the param so
            // the dialog stays closed and the URL reflects the listing.
            if (params.jobId) navigate('/jobs', { replace: true });
          }}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

/**
 * PublicHeader
 *
 * Sticky top bar shown on all public pages. Auth-aware:
 *   • Anonymous  → "Candidate Login" button → opens CandidateLoginDialog
 *   • Logged in  → avatar dropdown with email + Logout
 *
 * Login deliberately uses the popup (not a redirect to /login_user)
 * because the candidate is already on a job listing — taking them to
 * a separate login page and bringing them back is jarring.
 */
export function PublicHeader({ companyName, companyLogoUrl } = {}) {
  const [me, setMe] = useState(null);
  const [showLogin, setShowLogin] = useState(false);
  const [headerLogoError, setHeaderLogoError] = useState(false);

  // Refresh the auth state every time the navbar mounts so a tab that's
  // been open for a while picks up logout/login that happened elsewhere.
  const refreshMe = async () => {
    try {
      const data = await getMe();
      setMe(data);
    } catch (e) {
      setMe({ logged_in: false });
    }
  };

  useEffect(() => {
    refreshMe();
  }, []);

  const handleLogout = async () => {
    try {
      await logoutCandidate();
    } catch (e) {
      // Ignore — clearing client state below is what matters
    }
    setMe({ logged_in: false });
  };

  // Pull a nice display label: full name if we have it, else first part
  // of the email, else the literal email.
  const label = me?.full_name || (me?.email || '').split('@')[0] || 'Account';
  const initial = (label || 'U').trim().charAt(0).toUpperCase();

  return (
    <>
      <header className="sticky top-0 z-30 border-b border-border/40 bg-background/85 backdrop-blur supports-[backdrop-filter]:bg-background/60">
        <div className="mx-auto flex h-14 max-w-6xl items-center justify-between px-4">
          {companyName ? (
            /* Company page → lead with ONLY the company's logo + name. The
               "All jobs" button in the banner handles getting back to the
               aggregator, so the platform mark isn't needed here. */
            <Link to="/jobs" className="flex min-w-0 items-center gap-2">
              {companyLogoUrl && !headerLogoError ? (
                <img
                  src={companyLogoUrl}
                  alt={companyName}
                  className="h-8 w-8 shrink-0 rounded-md border border-border/60 object-contain"
                  onError={() => setHeaderLogoError(true)}
                />
              ) : (
                <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-primary/10 text-primary">
                  <Building2 className="h-4 w-4" />
                </span>
              )}
              <span className="truncate text-base font-semibold text-foreground">{companyName}</span>
            </Link>
          ) : (
            <Link to="/jobs" className="flex shrink-0 items-center gap-2">
              <div className="flex h-8 w-8 items-center justify-center rounded-md bg-primary text-primary-foreground">
                <Briefcase className="h-4 w-4" />
              </div>
              <span className="font-semibold">SearchJob.ai · Jobs</span>
            </Link>
          )}

          {me?.logged_in ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  className="flex items-center gap-2 px-2"
                >
                  <span className="flex h-7 w-7 items-center justify-center rounded-full bg-primary/10 text-xs font-semibold text-primary">
                    {initial}
                  </span>
                  <span className="hidden text-sm sm:inline">{label}</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel className="flex flex-col gap-0.5">
                  <span className="text-sm font-medium">
                    {me.full_name || 'Candidate'}
                  </span>
                  <span className="truncate text-xs text-muted-foreground">
                    {me.email}
                  </span>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={handleLogout}
                  className="text-destructive focus:text-destructive"
                >
                  <LogOut className="mr-2 h-4 w-4" />
                  Logout
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <Button
              variant="default"
              size="sm"
              onClick={() => setShowLogin(true)}
            >
              <LogIn className="mr-2 h-4 w-4" />
              Candidate Login
            </Button>
          )}
        </div>
      </header>

      {showLogin && (
        <CandidateLoginDialog
          onClose={() => setShowLogin(false)}
          onSuccess={() => {
            setShowLogin(false);
            refreshMe();
          }}
        />
      )}
    </>
  );
}

/**
 * PublicFooter
 *
 * The About / Contact links point to the existing `/hirewithus/...`
 * pages instead of root-level paths so the SPA doesn't fall through
 * to an unrelated catch-all route (which is what was happening before
 * — bare `/about` was hitting the admin shell).
 */
export function PublicFooter() {
  return (
    <footer className="border-t border-border/40 bg-card">
      <div className="mx-auto flex max-w-6xl flex-col items-center justify-between gap-2 px-4 py-6 sm:flex-row">
        <p className="text-xs text-muted-foreground">
          © {new Date().getFullYear()} SearchJob.ai · All open roles aggregated in one place.
        </p>
        <div className="flex gap-4 text-xs text-muted-foreground">
          <Link to="/hirewithus/about" className="hover:text-foreground">
            About
          </Link>
          <Link to="/hirewithus/contact" className="hover:text-foreground">
            Contact
          </Link>
        </div>
      </div>
    </footer>
  );
}

function CompanyGroup({ group, onJobClick, onCompanyClick }) {
  return (
    <div>
      <button
        onClick={onCompanyClick}
        className="group mb-3 flex w-full items-center gap-3 text-left"
      >
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
          <Building2 className="h-5 w-5" />
        </div>
        <div className="flex-1">
          <h2 className="text-base font-semibold group-hover:text-primary">
            {group.name}
          </h2>
          <p className="text-xs text-muted-foreground">
            {group.items.length} open role{group.items.length === 1 ? '' : 's'}
          </p>
        </div>
        <ArrowRight className="h-4 w-4 text-muted-foreground transition-transform group-hover:translate-x-0.5 group-hover:text-foreground" />
      </button>
      <Separator className="mb-3" />
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {group.items.map((job) => (
          <JobCard key={job.job_id} job={job} onClick={() => onJobClick(job)} />
        ))}
      </div>
    </div>
  );
}

export function JobCard({ job, onClick }) {
  return (
    <Card
      onClick={onClick}
      className="group cursor-pointer border border-border/40 transition-all hover:-translate-y-0.5 hover:border-primary/40 hover:shadow-md"
    >
      <CardContent className="flex h-full flex-col gap-3 p-4">
        <div>
          <h3 className="line-clamp-2 text-sm font-semibold group-hover:text-primary">
            {job.title}
          </h3>
          <p className="mt-1 truncate text-xs text-muted-foreground">
            {job.company_name}
          </p>
        </div>

        <div className="flex flex-wrap gap-1.5 text-xs text-muted-foreground">
          {job.location && (
            <Meta icon={MapPin} text={job.location} />
          )}
          {job.employment_type && (
            <Meta icon={Briefcase} text={job.employment_type} />
          )}
          {job.experience && job.experience !== 'Not specified' && (
            <Meta icon={Clock} text={job.experience} />
          )}
        </div>

        {job.short_note && (
          <p className="line-clamp-2 text-xs text-muted-foreground/90">
            {job.short_note}
          </p>
        )}

        <div className="mt-auto flex items-center justify-between">
          {(() => {
            const salaryLabel = formatSalaryDisplay(job.salary_range);
            return salaryLabel !== 'Not specified' ? (
              <Badge variant="outline" className="border-0 bg-primary/5 text-primary">
                {salaryLabel}
              </Badge>
            ) : (
              <span />
            );
          })()}
          <span className="text-xs font-medium text-primary opacity-0 transition-opacity group-hover:opacity-100">
            View details →
          </span>
        </div>
      </CardContent>
    </Card>
  );
}

function Meta({ icon: Icon, text }) {
  return (
    <span className="flex items-center gap-1">
      <Icon className="h-3 w-3" />
      <span className="truncate">{text}</span>
    </span>
  );
}

export function ListSkeleton() {
  return (
    <div className="space-y-8">
      {Array.from({ length: 2 }).map((_, gi) => (
        <div key={gi}>
          <Skeleton className="mb-3 h-10 w-1/3" />
          <Separator className="mb-3" />
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {Array.from({ length: 3 }).map((_, i) => (
              <Skeleton key={i} className="h-40 w-full rounded-lg" />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

export function EmptyBlock({ query }) {
  return (
    <Card className="border border-dashed border-border/40">
      <CardContent className="flex flex-col items-center gap-3 py-16 text-center">
        <Inbox className="h-12 w-12 text-muted-foreground/50" />
        <h3 className="text-base font-semibold">No jobs found</h3>
        <p className="text-sm text-muted-foreground">
          {query
            ? `No active jobs match "${query}". Try a different search.`
            : 'No jobs are currently open. Check back soon.'}
        </p>
      </CardContent>
    </Card>
  );
}

export function ErrorBlock({ message }) {
  return (
    <Card className="border-destructive/30">
      <CardContent className="flex flex-col items-center gap-3 py-16 text-center">
        <div className="flex h-12 w-12 items-center justify-center rounded-full bg-destructive/10 text-destructive">
          <AlertTriangle className="h-6 w-6" />
        </div>
        <h3 className="text-base font-semibold">Couldn't load jobs</h3>
        <p className="text-sm text-muted-foreground">{message}</p>
        <Button
          size="sm"
          variant="outline"
          onClick={() => window.location.reload()}
        >
          <RefreshCw className="mr-2 h-4 w-4" />
          Retry
        </Button>
      </CardContent>
    </Card>
  );
}
